import React, { useState, useCallback } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  disabled?: boolean;
  "aria-label"?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = "Поиск по названию файла",
      className,
      debounceMs = 300,
      disabled,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    // Debounced change handler
    const handleChange = useCallback(
      (newValue: string) => {
        setInternalValue(newValue);

        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }

        const timer = setTimeout(() => {
          onChange(newValue);
        }, debounceMs);

        setDebounceTimer(timer);
      },
      [onChange, debounceMs, debounceTimer]
    );

    // Immediate clear handler
    const handleClear = useCallback(() => {
      setInternalValue("");
      onChange("");
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
    }, [onChange, debounceTimer]);

    // Sync internal value with external value
    React.useEffect(() => {
      if (value !== internalValue) {
        setInternalValue(value);
      }
    }, [value, internalValue]);

    // Cleanup timer on unmount
    React.useEffect(() => {
      return () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
      };
    }, [debounceTimer]);

    const hasValue = internalValue.length > 0;

    return (
      <div className={cn("relative", className)}>
        <Search 
          size={16} 
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" 
        />
        <Input
          ref={ref}
          type="text"
          value={internalValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pl-9",
            hasValue && "pr-9"
          )}
          aria-label={ariaLabel || placeholder}
          {...props}
        />
        {hasValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={disabled}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0 hover:bg-muted"
            aria-label="Очистить поиск"
          >
            <X size={14} className="text-muted-foreground" />
          </Button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";